// src/ui/App.js
import { calculateFromNet, calculateFromGross } from '../core/calculator.js';
import { LanguageManager } from '../i18n/LanguageManager.js';
import { InstallManager } from '../pwa/installManager.js';
import { ThemeManager } from './ThemeManager.js';
import { Header } from './Header.js';
import { RateSelector } from './RateSelector.js';
import { AmountInput } from './AmountInput.js';
import { CurrencyDropdown } from './CurrencyDropdown.js';
import { ResultsPanel } from './ResultsPanel.js';

export class App {
  constructor() {
    this._lm = new LanguageManager();
    this._im = new InstallManager();
    this._tm = new ThemeManager();

    // State
    this._state = {
      rate: 20,
      currency: 'USD',
      ht: 0,
      vat: 0,
      ttc: 0,
    };

    // Components
    this._currencyDropdown = new CurrencyDropdown({
      onSelect: (code) => this._onCurrencyChange(code),
    });

    this._rateSelector = new RateSelector({
      langManager: this._lm,
      onRateChange: (rate) => this._onRateChange(rate),
    });

    this._amountInput = new AmountInput({
      langManager: this._lm,
      currencyDropdown: this._currencyDropdown,
      onHTChange: (val) => this._onHTChange(val),
      onTTCChange: (val) => this._onTTCChange(val),
    });

    this._resultsPanel = new ResultsPanel({
      langManager: this._lm,
    });

    this._header = new Header({
      langManager: this._lm,
      installManager: this._im,
      themeManager: this._tm,
    });
  }

  init() {
    this._lm.init();
    this._im.init();
    this._tm.init();

    // Detect currency from locale
    this._state.currency = this._lm.detectCurrency();
    this._amountInput.setCurrency(this._state.currency);
    this._currencyDropdown.setCurrent(this._state.currency);

    // Build DOM
    const root = document.getElementById('app');
    root.appendChild(this._header.element);

    const main = document.createElement('main');
    main.className = 'app-main';

    const card = document.createElement('div');
    card.className = 'calc-card';

    const leftPanel = document.createElement('div');
    leftPanel.className = 'panel-left';

    const title = document.createElement('p');
    title.className = 'panel-title';
    title.dataset.i18n = 'app.title';
    title.textContent = this._lm.t('app.title');

    const subtitle = document.createElement('p');
    subtitle.className = 'panel-subtitle';
    subtitle.dataset.i18n = 'app.subtitle';
    subtitle.textContent = this._lm.t('app.subtitle');

    leftPanel.appendChild(title);
    leftPanel.appendChild(subtitle);
    leftPanel.appendChild(this._rateSelector.element);
    leftPanel.appendChild(this._amountInput.element);

    card.appendChild(leftPanel);
    card.appendChild(this._resultsPanel.element);
    main.appendChild(card);

    // Currency dropdown appended to body (fixed positioning)
    document.body.appendChild(this._currencyDropdown.element);

    root.appendChild(main);

    // Initial render
    this._updateResults();
    this._resultsPanel.updateTranslations();

    // Language change handler
    this._lm.onChange(() => this._onLanguageChange());
  }

  _onRateChange(rate) {
    this._state.rate = rate;
    const { vat, ttc } = calculateFromNet(this._state.ht, rate);
    this._state.vat = vat;
    this._state.ttc = ttc;
    this._amountInput.setTTCValue(ttc);
    this._updateResults();
  }

  _onHTChange(val) {
    this._state.ht = val;
    const { vat, ttc } = calculateFromNet(val, this._state.rate);
    this._state.vat = vat;
    this._state.ttc = ttc;
    this._amountInput.setTTCValue(ttc);
    this._updateResults();
  }

  _onTTCChange(val) {
    this._state.ttc = val;
    const { ht, vat } = calculateFromGross(val, this._state.rate);
    this._state.ht = ht;
    this._state.vat = vat;
    this._amountInput.setHTValue(ht);
    this._updateResults();
  }

  _onCurrencyChange(code) {
    this._state.currency = code;
    this._amountInput.setCurrency(code);
    this._updateResults();
  }

  _onLanguageChange() {
    // Update data-i18n elements in left panel
    document.querySelectorAll('[data-i18n]').forEach((el) => {
      el.textContent = this._lm.t(el.dataset.i18n);
    });
    this._rateSelector.updateTranslations();
    this._amountInput.updateTranslations();
    this._resultsPanel.updateTranslations();
    this._header.updateTranslations();
    this._updateResults();
  }

  _updateResults() {
    this._resultsPanel.update({
      ht: this._state.ht,
      vat: this._state.vat,
      ttc: this._state.ttc,
      rate: this._state.rate,
      currency: this._state.currency,
      locale: this._lm.locale,
    });
  }
}
